import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Plus, Edit, Trash2, Building2, Target, TrendingUp, Upload, Download, Users, UserCog, Key } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ManagementPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('projects');
  const [loading, setLoading] = useState(true);
  
  // Project states
  const [projects, setProjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectName, setProjectName] = useState('');
  
  // Campaign states
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignFormData, setCampaignFormData] = useState({
    campaign_name: '',
    campaign_id: '',
    form_name: '',
    form_id: '',
    platform: '',
    project_id: ''
  });
  const [campaignPerformance, setCampaignPerformance] = useState(null);
  const [performanceDialogOpen, setPerformanceDialogOpen] = useState(false);
  const [campaignFile, setCampaignFile] = useState(null);
  const [uploadingCampaigns, setUploadingCampaigns] = useState(false);

  // Team states
  const [salesReps, setSalesReps] = useState([]);
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);
  const [editingProjects, setEditingProjects] = useState([]);
  const [editingName, setEditingName] = useState('');
  const [editingEmail, setEditingEmail] = useState('');
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [addMemberDialog, setAddMemberDialog] = useState(false);
  const [newMember, setNewMember] = useState({
    full_name: '',
    email: '',
    contact_no: '',
    user_role: 'Sales Rep',
    projects_assigned: []
  });

  // Password Reset states
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [confirmUserPassword, setConfirmUserPassword] = useState('');

  useEffect(() => {
    if (user?.user_role !== 'Super Administrator' && user?.user_role !== 'Manager') {
      navigate('/');
      return;
    }
    loadAllData();
  }, [user]);

  const loadAllData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [projectsRes, campaignsRes, salesRepsRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/brokers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProjects(projectsRes.data);
      setCampaigns(campaignsRes.data);
      setSalesReps(salesRepsRes.data);
      // For password reset, use the same brokers list (which contains all users)
      setAllUsers(salesRepsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ==================== PROJECT FUNCTIONS ====================
  const handleAddProject = () => {
    setEditingProject(null);
    setProjectName('');
    setDialogOpen(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setProjectName(project.project_name);
    setDialogOpen(true);
  };

  const handleSaveProject = async () => {
    if (!projectName.trim()) {
      toast.error('Project name is required');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, { project_name: projectName }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${API}/projects`, { project_name: projectName }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Project created successfully');
      }
      setDialogOpen(false);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleToggleProjectActive = async (projectId, currentStatus) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/projects/${projectId}`, { active_status: !currentStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Project status updated');
      loadAllData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"?`)) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Project deleted successfully');
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  // ==================== CAMPAIGN FUNCTIONS ====================
  const handleAddCampaign = () => {
    setEditingCampaign(null);
    setCampaignFormData({ campaign_name: '', campaign_id: '', form_name: '', form_id: '', platform: '', project_id: '' });
    setCampaignDialogOpen(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignFormData({
      campaign_name: campaign.campaign_name,
      campaign_id: campaign.campaign_id,
      form_name: campaign.form_name,
      form_id: campaign.form_id,
      platform: campaign.platform,
      project_id: campaign.project_id
    });
    setCampaignDialogOpen(true);
  };

  const handleSaveCampaign = async () => {
    if (!campaignFormData.campaign_name.trim() || !campaignFormData.campaign_id.trim() || 
        !campaignFormData.form_name.trim() || !campaignFormData.form_id.trim() || 
        !campaignFormData.platform.trim() || !campaignFormData.project_id.trim()) {
      toast.error('All campaign fields are required');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      if (editingCampaign) {
        await axios.put(`${API}/campaigns/${editingCampaign.id}`, campaignFormData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Campaign updated successfully');
      } else {
        await axios.post(`${API}/campaigns`, campaignFormData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Campaign created successfully');
      }
      setCampaignDialogOpen(false);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId, campaignName) => {
    if (!window.confirm(`Are you sure you want to delete "${campaignName}"?`)) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Campaign deleted successfully');
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete campaign');
    }
  };

  const handleViewPerformance = async (campaign) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.id}/performance`, { headers: { Authorization: `Bearer ${token}` } });
      setCampaignPerformance(response.data);
      setPerformanceDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load campaign performance');
    }
  };

  const downloadCampaignTemplate = () => {
    const csvContent = `campaign_name,campaign_id,form_name,form_id,platform,project_name
Marketing Campaign Q1,META_CAMP_001,Lead Gen Form,FORM_001,Facebook,Project Name`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'campaign_upload_template.csv';
    a.click();
  };

  const handleBulkUploadCampaigns = async () => {
    if (!campaignFile) {
      toast.error('Please select a file');
      return;
    }
    setUploadingCampaigns(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', campaignFile);
    try {
      const response = await axios.post(`${API}/campaigns/bulk-upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${response.data.campaigns_created} campaigns`);
      setCampaignFile(null);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload campaigns');
    } finally {
      setUploadingCampaigns(false);
    }
  };

  // ==================== TEAM FUNCTIONS ====================
  const handleEditSalesRep = (salesRep) => {
    setSelectedSalesRep(salesRep);
    setEditingProjects(salesRep.projects_assigned || []);
    setEditingName(salesRep.full_name || '');
    setEditingEmail(salesRep.email || '');
    setTeamDialogOpen(true);
  };

  const handleProjectToggle = (projectName) => {
    setEditingProjects(prev => prev.includes(projectName) ? prev.filter(p => p !== projectName) : [...prev, projectName]);
  };

  const handleSaveTeamMember = async () => {
    const token = localStorage.getItem('token');
    try {
      const updates = { projects_assigned: editingProjects };
      if (editingName && editingName !== selectedSalesRep.full_name) updates.full_name = editingName;
      if (editingEmail && editingEmail !== selectedSalesRep.email) updates.email = editingEmail;
      await axios.put(`${API}/brokers/${selectedSalesRep.id}`, updates, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Team member updated successfully');
      setTeamDialogOpen(false);
      loadAllData();
    } catch (error) {
      toast.error('Failed to update team member');
    }
  };

  const handleToggleTeamActive = async (salesRepId, currentStatus) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/brokers/${salesRepId}`, { active_status: !currentStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Status updated');
      loadAllData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddMember = async () => {
    if (!newMember.full_name || !newMember.email) {
      toast.error('Please fill required fields');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/auth/register`, {
        ...newMember,
        password: 'password123'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Team member added');
      setAddMemberDialog(false);
      setNewMember({ full_name: '', email: '', contact_no: '', user_role: 'Sales Rep', projects_assigned: [] });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  // ==================== PASSWORD RESET FUNCTIONS ====================
  const handleResetUserPassword = async () => {
    if (!selectedUser || !newUserPassword) {
      toast.error('Please select a user and enter new password');
      return;
    }
    if (newUserPassword !== confirmUserPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newUserPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/users/${selectedUser}/reset-password`, { new_password: newUserPassword }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password reset successfully');
      setSelectedUser('');
      setNewUserPassword('');
      setConfirmUserPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Manage Projects & Team
              </h1>
              <p className="text-sm text-slate-600">
                {projects.length} projects • {campaigns.length} campaigns • {salesReps.length} team members
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="projects" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Projects ({projects.length})
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Campaigns ({campaigns.length})
            </TabsTrigger>
            <TabsTrigger value="team" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Team ({salesReps.length})
            </TabsTrigger>
          </TabsList>

          {/* ==================== PROJECTS TAB ==================== */}
          <TabsContent value="projects">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">All Projects</h2>
              <Button onClick={handleAddProject} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="mr-2 h-4 w-4" />
                Add Project
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {projects.map(project => (
                <Card key={project.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1">
                        <Building2 className="h-5 w-5 text-cyan-600" />
                        <h3 className="font-semibold text-slate-800 text-sm">{project.project_name}</h3>
                      </div>
                      <Switch checked={project.active_status} onCheckedChange={() => handleToggleProjectActive(project.id, project.active_status)} />
                    </div>
                    <Badge className={project.active_status ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                      {project.active_status ? 'Active' : 'Inactive'}
                    </Badge>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => handleEditProject(project)} className="flex-1">
                        <Edit className="mr-1 h-3 w-3" />Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteProject(project.id, project.project_name)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ==================== CAMPAIGNS TAB ==================== */}
          <TabsContent value="campaigns">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">All Campaigns</h2>
              <div className="flex gap-2">
                <input type="file" id="campaign-upload" accept=".xlsx,.xls,.csv" onChange={(e) => setCampaignFile(e.target.files[0])} className="hidden" />
                <Button variant="outline" onClick={downloadCampaignTemplate} size="sm">
                  <Download className="mr-2 h-4 w-4" />Template
                </Button>
                <Button variant="outline" onClick={() => document.getElementById('campaign-upload').click()} size="sm">
                  <Upload className="mr-2 h-4 w-4" />{campaignFile ? campaignFile.name.substring(0, 10) + '...' : 'Upload CSV'}
                </Button>
                {campaignFile && (
                  <Button onClick={handleBulkUploadCampaigns} disabled={uploadingCampaigns} size="sm" className="bg-green-600 hover:bg-green-700">
                    {uploadingCampaigns ? 'Uploading...' : 'Process'}
                  </Button>
                )}
                <Button onClick={handleAddCampaign} className="bg-cyan-600 hover:bg-cyan-700">
                  <Plus className="mr-2 h-4 w-4" />Add Campaign
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.map(campaign => (
                <Card key={campaign.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Target className="h-5 w-5 text-blue-600" />
                      <h3 className="font-semibold text-slate-800 text-sm">{campaign.campaign_name}</h3>
                    </div>
                    <div className="space-y-1 mb-3 text-xs">
                      <div className="flex justify-between"><span className="text-slate-600">Platform:</span><Badge variant="outline">{campaign.platform}</Badge></div>
                      <div className="flex justify-between"><span className="text-slate-600">Project:</span><span className="font-medium">{campaign.project_name}</span></div>
                      <div className="flex justify-between"><span className="text-slate-600">Form:</span><span>{campaign.form_name}</span></div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleViewPerformance(campaign)} className="flex-1">
                        <TrendingUp className="mr-1 h-3 w-3" />Performance
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleEditCampaign(campaign)}><Edit className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteCampaign(campaign.id, campaign.campaign_name)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ==================== TEAM TAB ==================== */}
          <TabsContent value="team">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Team Members</h2>
              <Button onClick={() => setAddMemberDialog(true)} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="mr-2 h-4 w-4" />Add Team Member
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {salesReps.map(rep => (
                <Card key={rep.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{rep.full_name}</CardTitle>
                        <p className="text-xs text-slate-600 mt-1">{rep.email}</p>
                        <Badge className="mt-2" variant={rep.user_role === 'Super Administrator' ? 'default' : 'secondary'}>{rep.user_role}</Badge>
                      </div>
                      <Switch checked={rep.active_status} onCheckedChange={() => handleToggleTeamActive(rep.id, rep.active_status)} />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <div className="text-xs text-slate-600 mb-1">Projects Assigned:</div>
                      <div className="text-sm font-bold text-cyan-700">{rep.projects_assigned?.length || 0} / {projects.length}</div>
                    </div>
                    <Button size="sm" variant="outline" className="w-full" onClick={() => handleEditSalesRep(rep)}>
                      <Edit className="mr-2 h-3 w-3" />Assign Projects
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Password Reset Section - Super Admin Only */}
            {user?.user_role === 'Super Administrator' && (
              <Card className="border-purple-200 bg-purple-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-800">
                    <Key className="h-5 w-5" />
                    Reset User Passwords
                  </CardTitle>
                  <CardDescription className="text-purple-700">
                    Reset passwords for other users (Super Administrator only)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Select User</Label>
                      <Select value={selectedUser} onValueChange={setSelectedUser}>
                        <SelectTrigger><SelectValue placeholder="Choose a user" /></SelectTrigger>
                        <SelectContent>
                          {allUsers.filter(u => u.id !== user?.id).map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>New Password</Label>
                      <Input type="password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} placeholder="Enter new password" />
                    </div>
                    <div className="space-y-2">
                      <Label>Confirm Password</Label>
                      <Input type="password" value={confirmUserPassword} onChange={(e) => setConfirmUserPassword(e.target.value)} placeholder="Confirm password" />
                    </div>
                  </div>
                  <Button onClick={handleResetUserPassword} className="bg-purple-600 hover:bg-purple-700">
                    <Key className="mr-2 h-4 w-4" />Reset Password
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ==================== DIALOGS ==================== */}
      
      {/* Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingProject ? 'Edit Project' : 'Add Project'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Project Name</Label>
              <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="Enter project name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProject} className="bg-cyan-600 hover:bg-cyan-700">{editingProject ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Dialog */}
      <Dialog open={campaignDialogOpen} onOpenChange={setCampaignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Add Campaign'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Campaign Name</Label><Input value={campaignFormData.campaign_name} onChange={(e) => setCampaignFormData({...campaignFormData, campaign_name: e.target.value})} /></div>
            <div className="space-y-2"><Label>Campaign ID</Label><Input value={campaignFormData.campaign_id} onChange={(e) => setCampaignFormData({...campaignFormData, campaign_id: e.target.value})} /></div>
            <div className="space-y-2"><Label>Form Name</Label><Input value={campaignFormData.form_name} onChange={(e) => setCampaignFormData({...campaignFormData, form_name: e.target.value})} /></div>
            <div className="space-y-2"><Label>Form ID</Label><Input value={campaignFormData.form_id} onChange={(e) => setCampaignFormData({...campaignFormData, form_id: e.target.value})} /></div>
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={campaignFormData.platform} onValueChange={(v) => setCampaignFormData({...campaignFormData, platform: v})}>
                <SelectTrigger><SelectValue placeholder="Select platform" /></SelectTrigger>
                <SelectContent>
                  {['Facebook', 'Google', 'Instagram', 'LinkedIn', 'Other'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Project</Label>
              <Select value={campaignFormData.project_id} onValueChange={(v) => setCampaignFormData({...campaignFormData, project_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.filter(p => p.active_status).map(p => <SelectItem key={p.id} value={p.id}>{p.project_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCampaignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCampaign} className="bg-cyan-600 hover:bg-cyan-700">{editingCampaign ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Performance Dialog */}
      <Dialog open={performanceDialogOpen} onOpenChange={setPerformanceDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Campaign Performance</DialogTitle></DialogHeader>
          {campaignPerformance && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="text-center"><p className="text-2xl font-bold text-blue-600">{campaignPerformance.total_leads}</p><p className="text-sm text-slate-600">Total</p></div>
                <div className="text-center"><p className="text-2xl font-bold text-green-600">{campaignPerformance.conversion_metrics?.booked_leads || 0}</p><p className="text-sm text-slate-600">Booked</p></div>
                <div className="text-center"><p className="text-2xl font-bold text-purple-600">{campaignPerformance.conversion_metrics?.conversion_rate || 0}%</p><p className="text-sm text-slate-600">Conversion</p></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPerformanceDialogOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Edit Dialog */}
      <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit: {selectedSalesRep?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Full Name</Label><Input value={editingName} onChange={(e) => setEditingName(e.target.value)} /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={editingEmail} onChange={(e) => setEditingEmail(e.target.value)} /></div>
            <div className="space-y-2">
              <Label>Assign Projects</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 border rounded-md p-3">
                {projects.map(project => (
                  <div key={project.id} className="flex items-center space-x-2">
                    <Checkbox id={`edit-${project.id}`} checked={editingProjects.includes(project.project_name)} onCheckedChange={() => handleProjectToggle(project.project_name)} />
                    <label htmlFor={`edit-${project.id}`} className="text-sm cursor-pointer">{project.project_name}</label>
                  </div>
                ))}
              </div>
              <div className="text-sm text-slate-600">{editingProjects.length} project(s) selected</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTeamMember} className="bg-cyan-600 hover:bg-cyan-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialog} onOpenChange={setAddMemberDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Full Name *</Label><Input value={newMember.full_name} onChange={(e) => setNewMember({...newMember, full_name: e.target.value})} /></div>
              <div className="space-y-2"><Label>Email *</Label><Input type="email" value={newMember.email} onChange={(e) => setNewMember({...newMember, email: e.target.value})} /></div>
              <div className="space-y-2">
                <Label>Contact</Label>
                <Input 
                  value={newMember.contact_no} 
                  onChange={(e) => {
                    // Allow + prefix and digits, with optional formatting (spaces, dashes, parentheses)
                    let value = e.target.value;
                    // Remove all non-digit, non-formatting characters except + at start
                    value = value.replace(/[^\d+\s\-()]/g, '');
                    // Ensure + only appears at the start
                    if (value.includes('+') && !value.startsWith('+')) {
                      value = '+' + value.replace(/\+/g, '');
                    }
                    // Limit to 20 characters to allow for formatting
                    if (value.length <= 20) {
                      setNewMember({...newMember, contact_no: value});
                    }
                  }}
                  placeholder="Enter mobile number (e.g., +1234567890 or 1234567890)"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={newMember.user_role} onValueChange={(v) => setNewMember({...newMember, user_role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sales Rep">Sales Rep</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Super Administrator">Super Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Assign Projects</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border rounded-lg">
                {projects.map(project => (
                  <div key={project.id} className="flex items-center space-x-2">
                    <Checkbox id={`new-${project.id}`} checked={newMember.projects_assigned.includes(project.project_name)} onCheckedChange={() => setNewMember(prev => ({ ...prev, projects_assigned: prev.projects_assigned.includes(project.project_name) ? prev.projects_assigned.filter(p => p !== project.project_name) : [...prev.projects_assigned, project.project_name] }))} />
                    <label htmlFor={`new-${project.id}`} className="text-sm cursor-pointer">{project.project_name}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMemberDialog(false)}>Cancel</Button>
            <Button onClick={handleAddMember} className="bg-cyan-600 hover:bg-cyan-700">Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ManagementPage;
